import type {
  RefundOrderRecord,
  RefundOrderStatus,
  RefundOrders,
} from "@screen/Home/pages/OrderList/type/orderList";

/** 尚未結案（仍在跑）的狀態 */
const OPEN_STATUSES: RefundOrderStatus[] = ["bank", "processing", "manual", "failed"];

export const isRefundOpen = (s: RefundOrderStatus) => OPEN_STATUSES.includes(s);

export const hasRefundOrders = (refunds?: RefundOrders) =>
  !!refunds && refunds.state !== "none" && refunds.records.length > 0;

/** 頁首徽章：退款中／已退款（覆蓋配送狀態徽章） */
export function getRefundHeaderBadge(refunds?: RefundOrders): { label: string; done: boolean } | null {
  if (!hasRefundOrders(refunds)) return null;
  if (refunds!.state === "done") return { label: "已退款", done: true };
  return { label: "退款中", done: false };
}

/** 退款進度卡右上角徽章 */
export function getRefundCardBadge(record: RefundOrderRecord): string {
  if (record.status === "done") return "退款完成";
  if (record.status === "manual" || record.status === "failed") return "改匯款退回";
  if (record.status === "cancel") return "已取消";
  return "退款處理中";
}

export const REFUND_STATUS_LABEL: Record<RefundOrderStatus, string> = {
  bank: "處理中",
  processing: "處理中",
  manual: "處理中",
  failed: "處理中",
  done: "已退款",
  cancel: "已取消",
};

export const REFUND_STATUS_HINT: Record<RefundOrderStatus, string> = {
  bank: "已向發卡行送出，約 3–5 個工作天入帳",
  processing: "瓦斯行將以現金或轉帳退還",
  manual: "原路退款失敗，將改以匯款方式退回（可能收取匯款服務費），客服將與您聯繫確認帳戶。",
  failed: "原路退款失敗，將改以匯款方式退回（可能收取匯款服務費），客服將與您聯繫確認帳戶。",
  done: "退款已完成，發票將同步作廢 / 折讓。",
  cancel: "此筆退款已取消，訂單金額未變動",
};

export const REFUND_METHOD_LABEL: Record<string, string> = {
  electronic: "原卡退回",
  cash: "現金 / 轉帳",
};

export function isFullyRefunded(refunds: RefundOrders, orderTotal: number): boolean {
  return orderTotal > 0 && refunds.refunded_total >= orderTotal;
}

export function refundScopeLabel(refunds: RefundOrders, orderTotal: number): string {
  const committed = refunds.refunded_total + refunds.pending_total;
  return orderTotal > 0 && committed >= orderTotal ? "全額退" : "部分退";
}

export function refundScopeLabelForRecord(record: RefundOrderRecord, orderTotal: number): string {
  return orderTotal > 0 && record.actual_refund >= orderTotal ? "全額退" : "部分退";
}

export function hasFeeBreakdown(r: RefundOrderRecord): boolean {
  return r.actual_refund !== r.refund_amount;
}

/** 進行中的排前面，其餘依建立時間新到舊 */
export function sortRefundRecords(records: RefundOrderRecord[]): RefundOrderRecord[] {
  return [...records].sort((a, b) => {
    const aOpen = isRefundOpen(a.status) ? 1 : 0;
    const bOpen = isRefundOpen(b.status) ? 1 : 0;
    if (aOpen !== bOpen) return bOpen - aOpen;
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
}

export type RefundStepIcon = "check" | "number" | "warn";

/** 附圖橫向三步驟 */
export interface RefundStep {
  title: string;
  state: "done" | "active" | "todo";
  icon: RefundStepIcon;
  stepNumber?: number;
}

/**
 * 對齊設計稿：
 * - 一般電子：已受理 → 銀行處理中 → 已完成
 * - 現金：已受理 → 現金 / 轉帳退款 → 已完成
 * - 失敗改匯：已受理 → 退款失敗 → 改匯款處理
 */
export function buildRefundSteps(r: RefundOrderRecord): RefundStep[] {
  const isDone = r.status === "done";
  const isCancel = r.status === "cancel";
  const isManualPath = r.status === "manual" || r.status === "failed" || !!r.events.find((e) => e.event_type === "manual" || e.event_type === "bank_fail");

  if (isCancel) {
    return [
      { title: "已受理", state: "done", icon: "check" },
      { title: "已取消", state: "done", icon: "check" },
    ];
  }

  if (isManualPath && !isDone) {
    return [
      { title: "已受理", state: "done", icon: "check" },
      { title: "退款失敗", state: "done", icon: "warn" },
      {
        title: "改匯款處理",
        state: r.status === "manual" ? "active" : "done",
        icon: r.status === "manual" ? "check" : "check",
      },
    ];
  }

  if (isManualPath && isDone) {
    return [
      { title: "已受理", state: "done", icon: "check" },
      { title: "退款失敗", state: "done", icon: "warn" },
      { title: "改匯款處理", state: "done", icon: "check" },
    ];
  }

  const middleTitle = r.refund_method === "cash" ? "現金處理中" : "銀行處理中";

  return [
    { title: "已受理", state: "done", icon: "check" },
    {
      title: middleTitle,
      state: isDone ? "done" : "active",
      icon: isDone ? "check" : "number",
      stepNumber: 2,
    },
    {
      title: "已完成",
      state: isDone ? "done" : "todo",
      icon: isDone ? "check" : "number",
      stepNumber: 3,
    },
  ];
}

/** 預計到帳文案（僅電子退款進行中顯示） */
export function etaLabel(record: RefundOrderRecord): string | null {
  if (record.status === "bank") return "3–5 個工作天";
  return null;
}
